import { defineStore } from "pinia"
import { useAccountStore } from "@/stores/account"
import { updateGa4UserId, ga4Event, ga4ScreenView } from "@/utils/gtag_utils"
import { putHubSpotContact, sendUserAgentToHubSpot } from "@/utils/hubspot_utils"
import type { RouteLocationNormalized } from "vue-router"
import { routeIdOf } from "@/utils/resource_ids"
import { useLoginStore } from "@/stores/login"
import { computed } from "vue"
import { useClarity } from "@/composables/clarity"

const clarity = useClarity()

export const useTrackingsStore = defineStore('trackings', () => {
  const updateTrackingUser = async (accountId: string) => {
    const accountStore = useAccountStore()
    const accountInfo = await accountStore.getAccountInfo({ request_current_disk_byte: false });
    if (!accountInfo)  return

    updateGa4UserId(accountId)

    clarity.identify(accountId)
    clarity.upgrade('')

    putHubSpotContact({ email: accountInfo.mail }, "")
    sendUserAgentToHubSpot()
  }

  const trackRouteView = (route: RouteLocationNormalized) => {
    if(!route.name) return;
    const routeName = route.name.toString()
    const routeId = routeIdOf(routeName);

    const eventParams: Record<string, string> = {}
    if ("cloudConstructionId" in route.params) {
      eventParams["工事ID"] = route.params.cloudConstructionId as string
    }
    if ("construction_id" in route.query) {
      eventParams["工事ID"] = route.query.construction_id as string
    }

    if ("photoKey" in route.params) {
      eventParams["写真キー"] = route.params.photoKey as string
    }
    if ("photo_key" in route.query) {
      eventParams["写真キー"] = route.query.photo_key as string
    }

    if ("kokubanHash" in route.params) {
      eventParams["黒板ハッシュ"] = route.params.kokubanHash as string
    }
    if ("kokubah_hash" in route.query) {
      eventParams["黒板ハッシュ"] = route.query.kokubah_hash as string
    }

    ga4ScreenView(route.fullPath)

    ga4Event(
      `表示_${routeName}`,
      {
        screen_name: `${routeId}-${routeName}`,
        event_action: '画面遷移',
        value: routeId,
        routeId,
        routeName,
        ...eventParams,
      }
    )
  }

  const loginStore = useLoginStore()
  const accountId = computed(() => {
    return loginStore.currentAccount?.account_id;
  })

  const trackDaichoViewEvent = (eventId: DaichoViewEventId, constructionId: string)  => {
    const eventName = getDaichoViewEventNameFromEventId(eventId)
    console.info('daichoViewEvent:', eventName, `(${eventId})`, )
    ga4Event(eventName, {
      "工事ID": constructionId,
      constructionId,
      eventId,
      account_id: accountId.value,
      transport_type: 'beacon',
    })
  }

  const trackKuradenEvent = (eventId: KuradenEventId, constructionId?: string) => {
    const eventName = getKuradenEventNameFromEventId(eventId)
    const eventParams: Record<string, any> = {
      eventId,
      account_id: accountId.value,
      transport_type: 'beacon',
    }

    if (constructionId) {
      eventParams["工事ID"] = constructionId
      eventParams.constructionId = constructionId
    }

    ga4Event(eventName, eventParams)
  }

  const trackConstructionListEvent = (eventId: ConstructionListEventId, constructionId?: string) => {
    const eventName = getConstructionListEventNameFromEventId(eventId)
    const eventParams: Record<string, any> = {
      eventId,
      account_id: accountId.value,
      transport_type: 'beacon',
    }

    if (constructionId) {
      eventParams["工事ID"] = constructionId
      eventParams.constructionId = constructionId
    }

    ga4Event(eventName, eventParams)
  }

  const trackConstructionCreateEvent = (eventId: ConstructionCreateEventId, constructionId?: string) => {
    const eventName = getConstructionCreateEventNameFromEventId(eventId)
    const eventParams: Record<string, any> = {
      eventId,
      account_id: accountId.value,
      transport_type: 'beacon',
    }

    if (constructionId) {
      eventParams["工事ID"] = constructionId
      eventParams.constructionId = constructionId
    }

    ga4Event(eventName, eventParams)
  }

  const trackConstructionSettingEvent = (eventId: ConstructionSettingEventId, constructionId: string) => {
    const eventName = getConstructionSettingEventNameFromEventId(eventId)
    ga4Event(eventName, {
      "工事ID": constructionId,
      constructionId,
      eventId,
      account_id: accountId.value,
      transport_type: 'beacon',
    })
  }

  return {
    updateTrackingUser,
    trackRouteView,
    trackDaichoViewEvent,
    trackKuradenEvent,
    trackConstructionListEvent,
    trackConstructionCreateEvent,
    trackConstructionSettingEvent,
  }
})

// 台帳ビューのイベント
const DAICHO_VIEW_TRACKING_EVENTS = {
  constructionListPhoto: "台帳ビュー_工事一覧_写真",
  constructionListKokuban: "台帳ビュー_工事一覧_黒板",
  treeViewDialogFreePlanShowPremium: "台帳ビュー_ダイアログ_フリー向け_プレミアムを詳しく見る",
  treeViewDialogFreePlanClose: "台帳ビュー_ダイアログ_フリー向け_閉じる",
  treeViewDialogGeneralPermissionOk: "台帳ビュー_ダイアログ_一般向け_OK",
  treeViewDialogExistingConstructionOk: "台帳ビュー_ダイアログ_既存工事_OK",
  treeViewDialogCalsOk: "台帳ビュー_ダイアログ_電子納品_OK",
  shareSelect: "台帳ビュー_共有_選択",
  shareOtherMember: "台帳ビュー_共有_上記の人以外に共有する",
  shareInputMessage: "台帳ビュー_共有_メッセージ入力欄",
  shareSend: "台帳ビュー_共有_送信",
  shareClose: "台帳ビュー_共有_閉じる",
  shareOtherMemberMailAddress: "台帳ビュー_共有_共有相手のメールアドレス",
  shareOtherMemberName: "台帳ビュー_共有_あなたの名前",
  shareOtherMemberMessage: "台帳ビュー_共有_メッセージ",
  shareOtherMemberBack: "台帳ビュー_共有_戻る",
} as const;
type DaichoViewEventId = keyof typeof DAICHO_VIEW_TRACKING_EVENTS;
const getDaichoViewEventNameFromEventId = (eventId: DaichoViewEventId) => (DAICHO_VIEW_TRACKING_EVENTS[eventId])

// 蔵電のイベント
const KURADEN_TRACKING_EVENTS = {
  constructionListHeaderPhoneWhoToCallGroupModeOff: "工事一覧_ヘッダー電話_だれに話しますか？_グループモード解除",
  constructionInnerHeaderPhoneWhoToCallGroupModeOff: "工事内_ヘッダー電話_だれに話しますか？_グループモード解除",
  photoDetailKuradenIcon: "写真詳細__蔵電アイコン",
  photoDetailWhoToCallClose: "写真詳細__だれに電話しますか？_とじる",
  photoDetailWhoToCallStart: "写真詳細__だれに電話しますか？_通話を開始",
  photoDetailWhoToCallGroupMode: "写真詳細__だれに電話しますか？_グループモード",
  photoDetailWhoToCallGroupModeOff: "写真詳細__だれに電話しますか？_グループモード解除",
  zumenDetailKuradenIcon: "図面詳細__蔵電アイコン",
  zumenDetailWhoToCallClose: "図面詳細__だれに電話しますか？_とじる",
  zumenDetailWhoToCallStart: "図面詳細__だれに電話しますか？_通話を開始",
  zumenDetailWhoToCallGroupMode: "図面詳細__だれに電話しますか？_グループモード",
  zumenDetailWhoToCallGroupModeOff: "図面詳細__だれに電話しますか？_グループモード解除",
  kokubanDetailKuradenIcon: "黒板詳細__蔵電アイコン",
  kokubanDetailWhoToCallClose: "黒板詳細__だれに電話しますか？_とじる",
  kokubanDetailWhoToCallStart: "黒板詳細__だれに電話しますか？_通話を開始",
  kokubanDetailWhoToCallGroupMode: "黒板詳細__だれに電話しますか？_グループモード",
  kokubanDetailWhoToCallGroupModeOff: "黒板詳細__だれに電話しますか？_グループモード解除",
  incomingCallDialogReject: "受電ダイアログ__拒否",
  incomingCallDialogAnswer: "受電ダイアログ__応答",
  operationLockToast: "蔵クラWeb_画面共有_クラ電中は操作できません",
} as const;
export type KuradenEventId = keyof typeof KURADEN_TRACKING_EVENTS;
const getKuradenEventNameFromEventId = (eventId: KuradenEventId) => (KURADEN_TRACKING_EVENTS[eventId])

// 工事一覧のイベント
const CONSTRUCTION_LIST_TRACKING_EVENTS = {
  constructionListSearchInput: "工事一覧_検索バー_工事名入力",
  constructionListSearchButton: "工事一覧_検索バー_検索ボタン",
  constructionListCardTap: "工事一覧_工事カード_工事カードタップ",
  constructionListSettingIcon: "工事一覧_工事カード_設定アイコン",
  constructionListLeaveProject: "工事一覧_工事カード_工事から退出アイコン",
  constructionListDeletedItems: "工事一覧_工事カード_削除した項目",
  constructionListAdminAvatar: "工事一覧_工事カード_管理者_アバター",
  constructionListAdminMore: "工事一覧_工事カード_管理者_…ボタン",
  constructionListMemberAvatar: "工事一覧_工事カード_担当者_アバター",
  constructionListMemberMore: "工事一覧_工事カード_担当者_…ボタン",
  constructionListDeviceIcon: "工事一覧_工事カード_デバイス_デバイスアイコン",
  constructionListDeviceAdd: "工事一覧_工事カード_デバイス_＋ボタン",
  constructionListPhoto: "工事一覧_工事カード_メニュー_写真",
  constructionListKokuban: "工事一覧_工事カード_メニュー_黒板",
  constructionListZumen: "工事一覧_工事カード_メニュー_図面",
  constructionListPost: "工事一覧_工事カード_メニュー_ポスト",
  constructionListAddButton: "工事一覧_工事追加_＋ボタン（フローティング）",
  // 工事招待（工事一覧から工事に参加）
  constructionListInviteNotificationDetail: "工事一覧_招待通知_クリックで確認",
  constructionListInviteListClose: "工事一覧_招待一覧_とじる",
  constructionListInviteCardJoin: "工事一覧_招待一覧_招待カード_参加",
  constructionListInviteCardDecline: "工事一覧_招待一覧_招待カード_辞退",
  constructionListInviteLimitDialogClose: "工事一覧_参加上限ダイアログ_とじる",
  constructionListInviteLimitDialogForceJoin: "工事一覧_参加上限ダイアログ_今の工事を退出して参加",
  constructionListInviteLimitDialogShowPremium: "工事一覧_参加上限ダイアログ_『プレミアム』を詳しく見る",
} as const;
export type ConstructionListEventId = keyof typeof CONSTRUCTION_LIST_TRACKING_EVENTS;
const getConstructionListEventNameFromEventId = (eventId: ConstructionListEventId) => (CONSTRUCTION_LIST_TRACKING_EVENTS[eventId])

// 工事作成のイベント
const CONSTRUCTION_CREATE_TRACKING_EVENTS = {
  constructionCreateNameInput: "工事作成_工事名入力_工事名入力欄",
  constructionCreateNameNext: "工事作成_工事名入力_次へ",
  constructionCreateOrganizeBack: "工事作成_整理情報有無選択_戻るボタン",
  constructionCreateOrganizeYes: "工事作成_整理情報有無選択_する",
  constructionCreateOrganizeNo: "工事作成_整理情報有無選択_しない",
  constructionCreateOrganizeNext: "工事作成_整理情報有無選択_次へ",
  constructionCreateOrganizeReturn: "工事作成_整理情報有無選択_もどる",
  constructionCreateFolderBack: "工事作成_整理情報設定_戻るボタン",
  constructionCreateFolderCategoryBusiness: "工事作成_整理情報設定_フォルダ構成のマスタ_事業区分",
  constructionCreateFolderCategoryWork: "工事作成_整理情報設定_フォルダ構成のマスタ_工事区分",
  constructionCreateFolderSearchInput: "工事作成_整理情報設定_フォルダ構成のマスタ_検索入力欄",
  constructionCreateFolderSearchButton: "工事作成_整理情報設定_フォルダ構成のマスタ_検索ボタン",
  constructionCreateFolderMasterCheckbox: "工事作成_整理情報設定_フォルダ構成のマスタ_工種チェックボックス",
  constructionCreateFolderAddButton: "工事作成_整理情報設定_追加ボタン（>>）",
  constructionCreateFolderTreeItem: "工事作成_整理情報設定_このフォルダ構成_ツリーアイテム",
  constructionCreateFolderSave: "工事作成_整理情報設定_設定する",
  constructionCreateCalsBack: "工事作成_電子納品有無選択_戻るボタン",
  constructionCreateCalsYes: "工事作成_電子納品有無選択_電子納品する",
  constructionCreateCalsNo: "工事作成_電子納品有無選択_しない",
  constructionCreateCalsNext: "工事作成_電子納品有無選択_次へ",
  constructionCreateCalsReturn: "工事作成_電子納品有無選択_もどる",
  constructionCreateNewFeatureClose: "工事作成_新機能利用選択_とじる",
  constructionCreateNewFeatureTry: "工事作成_新機能利用選択_試す",
  constructionCreateNewFeatureAsUsual: "工事作成_新機能利用選択_これまで通りの工事を作成",
  constructionCreateSelectTypeClose: "工事作成_工事種類選択_とじる",
  constructionCreateSelectTypeBeta: "工事作成_工事種類選択_ベータ版を体験",
  constructionCreateSelectTypeAsUsual: "工事作成_工事種類選択_工事を作成",
  constructionCreateCopyDialogNo: "工事作成_工事コピーダイアログ_しない",
  constructionCreateCopyDialogCopy: "工事作成_工事コピーダイアログ_コピーする",
  constructionCreateCopySelectBack: "工事作成_コピーする工事の選択_戻るボタン",
  constructionCreateCopySelectCopy: "工事作成_コピーする工事の選択_コピーする",
  constructionCreateFolderAddMasterButton: "工事作成_整理情報設定_マスタを追加ボタン",
  constructionCreateFolderExcelAddMasterClose: "工事作成_整理情報設定_Excelからマスタを追加_とじる",
  constructionCreateFolderExcelAddMasterTemplateDownload: "工事作成_整理情報設定_Excelからマスタを追加_ひな型のダウンロード",
  constructionCreateFolderExcelAddMasterSelectFile: "工事作成_整理情報設定_Excelからマスタを追加_ファイルを選択",
  constructionCreateFolderExcelAddMasterAdd: "工事作成_整理情報設定_Excelからマスタを追加_追加する",
} as const;
export type ConstructionCreateEventId = keyof typeof CONSTRUCTION_CREATE_TRACKING_EVENTS;
const getConstructionCreateEventNameFromEventId = (eventId: ConstructionCreateEventId) => (CONSTRUCTION_CREATE_TRACKING_EVENTS[eventId])

// 工事の設定のイベント
const CONSTRUCTION_SETTING_TRACKING_EVENTS = {
  constructionSettingMemberManagement: "工事の設定_一覧_メンバー管理",
  constructionSettingInviteLimit: "工事の設定_一覧_招待の制限",
  constructionSettingKokubanDefault: "工事の設定_一覧_黒板作成時の規定値",
  constructionSettingAdminName: "工事の設定_一覧_管理用工事名",
  constructionSettingFolderStructure: "工事の設定_一覧_工事写真の分類（フォルダ構成）",
  constructionSettingMemberBack: "工事の設定_工事のメンバー管理_戻るボタン",
  constructionSettingMemberTalk: "工事の設定_工事のメンバー管理_メンバー行_トークアイコン",
  constructionSettingMemberExternalLink: "工事の設定_工事のメンバー管理_メンバー行_外部リンクアイコン",
  constructionSettingMemberAddAdmin: "工事の設定_工事のメンバー管理_＋管理者を追加する",
  constructionSettingAddAdminEmailInput: "工事の設定_管理者の追加_追加する管理者_メールアドレス入力欄",
  constructionSettingAddAdminNameInput: "工事の設定_管理者の追加_追加する管理者_あなたの名前",
  constructionSettingAddAdminMessageInput: "工事の設定_管理者の追加_追加する管理者_メッセージ",
  constructionSettingAddAdminNext: "工事の設定_管理者の追加_次へ",
  constructionSettingAddAdminInviteClose: "工事の設定_管理者の追加_招待完了_閉じる",
  constructionSettingFolderBack: "工事の設定_整理情報設定_戻るボタン",
  constructionSettingFolderCategoryBusiness: "工事の設定_整理情報設定_フォルダ構成のマスタ_事業区分",
  constructionSettingFolderCategoryWork: "工事の設定_整理情報設定_フォルダ構成のマスタ_工事区分",
  constructionSettingFolderSearchInput: "工事の設定_整理情報設定_フォルダ構成のマスタ_検索入力欄",
  constructionSettingFolderSearchButton: "工事の設定_整理情報設定_フォルダ構成のマスタ_検索ボタン",
  constructionSettingFolderMasterCheckbox: "工事の設定_整理情報設定_フォルダ構成のマスタ_工種チェックボックス",
  constructionSettingFolderAddButton: "工事の設定_整理情報設定_追加ボタン",
  constructionSettingFolderTreeItem: "工事の設定_整理情報設定_このフォルダ構成_ツリーアイテム",
  constructionSettingFolderSave: "工事の設定_整理情報設定_設定する",
  constructionSettingFolderAddMasterButton: "工事の設定_整理情報設定_マスタを追加ボタン",
  constructionSettingFolderExcelAddMasterClose: "工事の設定_整理情報設定_Excelからマスタを追加_とじる",
  constructionSettingFolderExcelAddMasterTemplateDownload: "工事の設定_整理情報設定_Excelからマスタを追加_ひな型のダウンロード",
  constructionSettingFolderExcelAddMasterSelectFile: "工事の設定_整理情報設定_Excelからマスタを追加_ファイルを選択",
  constructionSettingFolderExcelAddMasterAdd: "工事の設定_整理情報設定_Excelからマスタを追加_追加する",
} as const;
export type ConstructionSettingEventId = keyof typeof CONSTRUCTION_SETTING_TRACKING_EVENTS;
const getConstructionSettingEventNameFromEventId = (eventId: ConstructionSettingEventId) => (CONSTRUCTION_SETTING_TRACKING_EVENTS[eventId])